import {
  type Cell,
  columnFilteringFeature,
  type ColumnHelper,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createColumnHelper,
  createFilteredRowModel,
  createSortedRowModel,
  type Header,
  type ReactTable,
  type Row,
  type RowData,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
  type TableOptions,
  type TableState,
} from "@tanstack/react-table";

export const dashboardTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnSizingFeature,
  columnResizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
});

/** Table instance type for Dashboard tables using the shared feature set and row data shape. */
export type DashboardTable<TData extends RowData> = ReactTable<typeof dashboardTableFeatures, TData>;
/** Row instance type for Dashboard tables using the shared feature set and row data shape. */
export type DashboardRow<TData extends RowData> = Row<typeof dashboardTableFeatures, TData>;
/** Cell instance type for Dashboard tables using the shared feature set and row data shape. */
export type DashboardCell<TData extends RowData> = Cell<typeof dashboardTableFeatures, TData, unknown>;
/** Header instance type for Dashboard tables using the shared feature set and row data shape. */
export type DashboardHeader<TData extends RowData> = Header<typeof dashboardTableFeatures, TData, unknown>;
/** Registered state shape for Dashboard tables using the shared feature set. */
export type DashboardTableState = TableState<typeof dashboardTableFeatures>;

/** Creates a column helper bound to the shared Dashboard table features for the provided row data shape. */
export function createDashboardColumnHelper<TData extends RowData>(): ColumnHelper<
  typeof dashboardTableFeatures,
  TData
> {
  return createColumnHelper<typeof dashboardTableFeatures, TData>();
}

type CustomTableColumnType =
  | "ACTION"
  | "VISUAL_INDICATOR"
  | "DATE"
  | "DATE_TIME"
  | "STATUS"
  | "USER"
  | "TEXT"
  | "LEADING_CONTENT"
  | "COUNT"
  | "TAGS";

export const TABLE_COLUMN_SIZES = {
  // e.g. Ticket visibility indicator
  VISUAL_INDICATOR: {
    MIN: 36,
    DEFAULT: 36,
  },
  // e.g. Share, Context menu
  ACTION: {
    MIN: 64,
    DEFAULT: 64,
  },
  // e.g. Status of a user
  STATUS: {
    MIN: 64,
    DEFAULT: 140,
  },
  // e.g. Amount of survey responses, Amount of addresses
  COUNT: {
    MIN: 64,
    DEFAULT: 140,
  },
  // e.g. Invite date of a user, Creation date of a ticket
  DATE: {
    MIN: 64,
    DEFAULT: 140,
  },
  // e.g. Date + time of a reservation
  DATE_TIME: {
    MIN: 100,
    DEFAULT: 200,
  },
  // e.g. Assignee of a ticket
  USER: {
    MIN: 100,
    DEFAULT: 240,
  },
  // Generic textual content
  TEXT: {
    MIN: 64,
    DEFAULT: 200,
  },
  // Leading textual content, e.g. Ticket title, Building name
  LEADING_CONTENT: {
    MIN: 100,
    DEFAULT: 320,
  },
  // e.g. List of projects of a platform group
  TAGS: {
    MIN: 200,
    DEFAULT: 400,
  },
} satisfies Partial<Record<CustomTableColumnType, { MIN: number; DEFAULT: number }>>;

// Use dynamic cell size based on resizing state, falling back to defined column size if not resizing
type TableCellWithSize = {
  getSize?: () => number;
  column: {
    getSize: () => number;
  };
};

export const getTableCellWidth = (cell: TableCellWithSize): number => {
  return Math.round(cell.getSize ? cell.getSize() : cell.column.getSize());
};

export const resizableTableProps = {
  enableColumnResizing: true,
  columnResizeMode: "onChange",
} satisfies Partial<TableOptions<typeof dashboardTableFeatures, any>>;
